const termsEN = {
  title: "Terms of Service",
  effectiveDate: "October 14, 2026",
  intro: `Welcome to Agapaé. These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you") and EnQube SMPC ("Company", "we", "us"), a company registered in Patras, Greece, governing your access to and use of the Agapaé platform, including our website, applications, and related services (collectively, the "Platform").

By creating an account, accessing, or using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, please do not use the Platform.`,

  sections: [
    {
      heading: "1. Definitions",
      content: [
        `For the purposes of these Terms:`,
        {
          type: "list",
          items: [
            `"Platform" means the Agapaé web application, mobile interfaces, APIs, and all related services operated by EnQube SMPC.`,
            `"Couple" or "Event Owner" means a registered user who creates and manages a wedding or event on the Platform.`,
            `"Guest" means any individual who accesses an event on the Platform through an invitation or join code, whether or not they hold a registered account.`,
            `"Content" means any text, photographs, videos, audio, data, or other materials uploaded, submitted, or displayed on the Platform by a User.`,
            `"Event Data" means all information associated with a specific event, including event details, guest lists, media, schedules, and related configurations.`,
            `"Service Plan" means the tier of service selected by the Couple (Blossom, Bouquet, or Heirloom), each with specific features, storage limits, and pricing.`,
          ],
        },
      ],
    },
    {
      heading: "2. Eligibility and Account Registration",
      content: [
        `To create an account on the Platform, you must:`,
        {
          type: "list",
          items: [
            `Be at least eighteen (18) years of age or have reached the age of legal majority in your jurisdiction;`,
            `Have the legal capacity to enter into a binding agreement;`,
            `Provide accurate, complete, and current registration information, including a valid email address and a secure password.`,
          ],
        },
        `You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately at privacy@agapae.gr if you become aware of any unauthorised use of your account.`,
        `We reserve the right to suspend or terminate accounts that contain inaccurate information or violate these Terms.`,
      ],
    },
    {
      heading: "3. The Service",
      content: [
        `Agapaé is a wedding and event management platform that enables Couples to:`,
        {
          type: "list",
          items: [
            `Create and customise a digital wedding experience;`,
            `Share event details, schedules, and location information;`,
            `Invite guests to view event information and contribute media;`,
            `Collect and organise photographs and videos from their event;`,
            `Share their love story through narrative and timeline features;`,
            `Manage gift preferences and registry information;`,
            `Use an AI planning assistant (Éros) on eligible Service Plans.`,
          ],
        },
        `The Platform is provided on an "as is" and "as available" basis. We continuously work to improve the service but do not guarantee uninterrupted or error-free operation.`,
        {
          type: "emphasis",
          text: "AI Assistant (Éros)",
        },
        `On eligible Service Plans, the Platform offers Éros, an optional AI planning assistant that helps you organise your event. Éros is provided on an "as is" basis and may produce output that is inaccurate, incomplete, or unsuitable for your circumstances. It does not constitute professional, legal, financial, or medical advice. You remain responsible for reviewing its suggestions and for any decisions you make. For details of how data is processed when you use Éros — including the third-party AI providers involved — see our Privacy Policy.`,
      ],
    },
    {
      heading: "4. Service Plans and Payments",
      content: [
        `The Platform offers the following Service Plans:`,
        {
          type: "list",
          items: [
            `Blossom (Free): up to 100 photos (photo uploads only; video uploads are available on Bouquet and Heirloom), up to 20 guests, 15 days post-event retention.`,
            `Bouquet (€99 one-time): 50 GB storage, up to 200 guests, 60 days post-event retention.`,
            `Heirloom (€249 one-time): 200 GB storage, up to 1,500 guests under fair use, 90 days post-event retention.`,
          ],
        },
        `Blossom events created before October 14, 2026 keep the limits of the earlier Blossom plan: up to 50 guests, photo and video uploads, and 2 GB of storage.`,
        `All prices are in Euros (EUR) and include applicable taxes unless otherwise stated. Payments are processed securely through Stripe. We do not store your payment card details on our servers.`,
        `We reserve the right to modify pricing for future purchases. Any price changes will not affect existing paid plans.`,
        `AI features such as the Éros planning assistant may be included with certain Service Plans purchased during a promotional or introductory period. Where an AI feature is included with your Service Plan at the time of purchase, it remains available for that event's planning in accordance with your plan. Outside such periods, AI features may be offered as a separate paid add-on. Availability, usage limits, and pricing of AI features may change for future purchases; such changes will not affect features already included with an existing paid plan, and we will provide reasonable notice of material changes.`,
      ],
    },
    {
      heading: "5. Your Content and the Memories Clause",
      content: [
        `Your memories belong to you. We believe that the photographs, videos, stories, and personal details you entrust to Agapaé are deeply personal, and we treat them accordingly.`,
        {
          type: "emphasis",
          text: "Ownership",
        },
        `You retain full and exclusive intellectual property ownership of all Content you upload, create, or submit to the Platform. Nothing in these Terms transfers ownership of your Content to us.`,
        {
          type: "emphasis",
          text: "Limited Licence",
        },
        `By uploading Content to the Platform, you grant EnQube SMPC a limited, non-exclusive, non-transferable, revocable licence to host, store, display, reproduce, and process your Content solely for the purpose of providing and improving the Service. This includes generating thumbnails, transcoding videos for optimal playback, and delivering your Content to authorised viewers.`,
        `This licence exists only for as long as your Content remains on the Platform and terminates automatically when you delete your Content or when your Event Data is removed in accordance with your Service Plan's retention period.`,
        `We will never sell, sublicence, or use your Content for advertising, marketing, or any purpose unrelated to the delivery of the Service.`,
      ],
    },
    {
      heading: "6. User-Generated Content",
      content: [
        `You are solely responsible for the Content you upload or share through the Platform. By submitting Content, you represent and warrant that:`,
        {
          type: "list",
          items: [
            `You own or have the necessary rights and permissions to upload and share the Content;`,
            `The Content does not infringe upon the intellectual property, privacy, or other rights of any third party;`,
            `The Content does not contain unlawful, defamatory, obscene, or otherwise objectionable material;`,
            `The Content does not contain malware, viruses, or other harmful code.`,
          ],
        },
        `We reserve the right to remove Content that violates these Terms or applicable law, without prior notice. For the avoidance of doubt, the exercise of this right to remove objectionable Content does not constitute a breach of the ownership guarantees or the Memories Clause set forth in Section 5.`,
      ],
    },
    {
      heading: "7. Guest Data Clause",
      content: [
        `As part of the Service, Couples may input, manage, and share personal data of their Guests, including names and email addresses.`,
        {
          type: "emphasis",
          text: "Couple Responsibility",
        },
        `When you, as a Couple, provide personal data about your Guests, you act as a data controller for that information under the General Data Protection Regulation (GDPR). You are responsible for ensuring that you have a lawful basis for sharing this data with the Platform — whether through the Guest's consent, legitimate interest arising from your wedding event, or another applicable legal basis.`,
        {
          type: "emphasis",
          text: "Platform Role",
        },
        `EnQube SMPC processes Guest data on behalf of the Couple as a data processor. We process Guest data solely for the purpose of delivering the Service and in accordance with the Couple's instructions, our Privacy Policy, and the Data Processing Addendum incorporated by reference into these Terms.`,
        `Guests who join an event provide their display name and, where required by the Couple, their email address. Guests may also upload Content to the event's shared gallery.`,
      ],
    },
    {
      heading: "8. Acceptable Use",
      content: [
        `You agree not to use the Platform to:`,
        {
          type: "list",
          items: [
            `Violate any applicable local, national, or international law or regulation;`,
            `Upload, share, or distribute Content that is unlawful, harmful, threatening, abusive, defamatory, or otherwise objectionable;`,
            `Impersonate any person or entity or misrepresent your affiliation;`,
            `Interfere with or disrupt the Platform's infrastructure, security, or other users' experience;`,
            `Attempt to gain unauthorised access to the Platform, other accounts, or systems;`,
            `Use automated scripts, bots, or scrapers to access or interact with the Platform;`,
            `Use the Platform for commercial purposes not authorised by these Terms;`,
            `Harvest, collect, or store personal data of other users without their consent.`,
          ],
        },
        `Violation of these provisions may result in immediate suspension or termination of your account.`,
      ],
    },
    {
      heading: "9. Vendors and Professional Users",
      content: [
        `The Platform may in the future offer features for wedding vendors and professionals. If and when such features become available, professional users will be subject to additional terms specific to commercial use.`,
        `The Platform does not currently operate as a marketplace. We do not endorse, guarantee, or assume responsibility for any services offered by third-party vendors, whether or not they are referenced on the Platform.`,
      ],
    },
    {
      heading: "10. Intellectual Property",
      content: [
        `The Platform — including its design, code, branding, logos, text, graphics, and all other proprietary materials — is owned by EnQube SMPC and protected by Greek, European, and international intellectual property laws.`,
        `You may not copy, modify, distribute, sell, or create derivative works based on the Platform or its proprietary materials without our prior written consent.`,
        `The name "Agapaé" and associated logos are trademarks of EnQube SMPC. Unauthorised use is prohibited.`,
      ],
    },
    {
      heading: "11. Right of Withdrawal",
      content: [
        `In accordance with EU Directive 2011/83/EU on consumer rights and Greek Law 2251/1994, if you are a consumer in the European Union, you have the right to withdraw from a purchase within fourteen (14) calendar days of the transaction, without providing a reason.`,
        {
          type: "emphasis",
          text: "Performance in Connection with the Wedding Date",
        },
        `The Platform is a service performed in connection with a specific event date — your wedding. By selecting a paid Service Plan, you acknowledge that the service is tied to that date and that performance is completed by the time your wedding takes place. Accordingly, in line with Article 16(a) of EU Directive 2011/83/EU, the right of withdrawal does not survive the performance of the service.`,
        {
          type: "emphasis",
          text: "Refund Policy",
        },
        `In practical terms, this means:`,
        {
          type: "list",
          items: [
            `You may request a refund within fourteen (14) calendar days of payment, provided your wedding has not yet taken place. In that case, we will refund the full amount paid for your Service Plan;`,
            `If your wedding date falls within the 14-day window, the right of withdrawal applies only until the day before your wedding. From the wedding date onward, the service is treated as fully performed and refunds are no longer available;`,
            `Once your wedding day has passed, no refund can be granted, as the service has been performed.`,
          ],
        },
        {
          type: "emphasis",
          text: "How to Request a Refund",
        },
        `To exercise your right of withdrawal, contact us at privacy@agapae.gr before the applicable deadline. A confirmation of these terms is included in your purchase confirmation email.`,
      ],
    },
    {
      heading: "12. Limitation of Liability",
      content: [
        `To the maximum extent permitted by applicable law, EnQube SMPC's total aggregate liability to you for any claims arising out of or related to these Terms or the Service shall not exceed the total fees paid by you to us in the twelve (12) months preceding the event giving rise to the claim.`,
        `This limitation does not apply to, and nothing in these Terms shall limit or exclude our liability for:`,
        {
          type: "list",
          items: [
            `Death or personal injury caused by our negligence;`,
            `Fraud or fraudulent misrepresentation;`,
            `Gross negligence or wilful misconduct;`,
            `Any liability that cannot be limited or excluded under applicable law, including mandatory consumer protection laws of the European Union or Greece.`,
          ],
        },
        `We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of or inability to use the Platform.`,
        `AI-generated suggestions are provided for convenience only and may be inaccurate or incomplete; to the maximum extent permitted by law, we are not liable for actions taken in reliance on AI-generated output.`,
        `We are not liable for the loss of Content due to circumstances beyond our reasonable control, including but not limited to data corruption, third-party service failures, third-party cloud infrastructure or hosting provider failures, or force majeure events. We strongly recommend that you maintain independent backups of important content.`,
      ],
    },
    {
      heading: "13. Indemnification",
      content: [
        `You agree to indemnify, defend, and hold harmless EnQube SMPC, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in connection with:`,
        {
          type: "list",
          items: [
            `Your use of the Platform;`,
            `Your breach of these Terms;`,
            `Your violation of any applicable law or regulation;`,
            `Any Content you upload, submit, or share through the Platform;`,
            `Your provision of Guest data to the Platform without an appropriate lawful basis.`,
          ],
        },
      ],
    },
    {
      heading: "14. Termination and Suspension",
      content: [
        {
          type: "emphasis",
          text: "Termination by You",
        },
        `You may terminate your account at any time by contacting us at privacy@agapae.gr. Upon termination, your Event Data will be retained for the remainder of the applicable retention period under your Service Plan, after which it will be permanently deleted.`,
        {
          type: "emphasis",
          text: "Termination or Suspension by Us",
        },
        `We may suspend or terminate your access to the Platform, with or without notice, if:`,
        {
          type: "list",
          items: [
            `You breach these Terms;`,
            `Your use of the Platform poses a security risk or may cause harm to other users;`,
            `We are required to do so by law;`,
            `We discontinue the Service (in which case we will provide reasonable notice and an opportunity to export your data).`,
          ],
        },
        {
          type: "emphasis",
          text: "Effect of Termination",
        },
        `Upon termination, your right to access the Platform ceases. Sections of these Terms that by their nature should survive termination (including Intellectual Property, Limitation of Liability, Indemnification, and Governing Law) shall continue in effect.`,
      ],
    },
    {
      heading: "15. Data Retention",
      content: [
        `Event Data, including uploaded media, is retained on the Platform according to your Service Plan:`,
        {
          type: "list",
          items: [
            `Blossom (Free): 15 days after your event date`,
            `Bouquet: 60 days after your event date`,
            `Heirloom: 90 days after your event date`,
          ],
        },
        `After the retention period expires, Event Data will be permanently deleted from our systems, including from cloud storage. This process is irreversible. The Platform will make reasonable efforts to send you email reminders — approximately seven (7) days and twenty-four (24) hours before permanent deletion — to the email address associated with your account. However, failure to receive such reminders does not extend the retention period or create any obligation to preserve Event Data beyond the stated timeframe.`,
        `We strongly encourage you to download and back up your Content before the retention period ends. The Platform provides download functionality for this purpose.`,
        `For further information on how we handle personal data, please refer to our Privacy Policy.`,
      ],
    },
    {
      heading: "16. Modifications to These Terms",
      content: [
        `We may update these Terms from time to time to reflect changes in our Service, legal requirements, or business practices. When we make material changes, we will:`,
        {
          type: "list",
          items: [
            `Update the "Last updated" date at the top of these Terms;`,
            `Notify registered users by email or through the Platform;`,
            `Provide at least thirty (30) days' notice before material changes take effect.`,
          ],
        },
        `Your continued use of the Platform after the updated Terms take effect constitutes acceptance of the changes. If you do not agree to the updated Terms, you may terminate your account.`,
      ],
    },
    {
      heading: "17. Severability",
      content: [
        `If any provision of these Terms is found to be invalid, unlawful, or unenforceable by a court of competent jurisdiction, that provision shall be modified to the minimum extent necessary to make it enforceable, or severed if modification is not possible. The remaining provisions shall continue in full force and effect.`,
      ],
    },
    {
      heading: "18. Governing Law and Jurisdiction",
      content: [
        `These Terms are governed by and construed in accordance with the laws of the Hellenic Republic (Greece), without regard to conflict of law principles.`,
        `Any disputes arising from or related to these Terms shall be subject to the exclusive jurisdiction of the courts of Athens, Greece.`,
        `Nothing in these Terms shall affect your rights as a consumer under the mandatory consumer protection laws of the European Union or your Member State of residence. Where such laws provide a higher level of protection, those provisions shall prevail.`,
      ],
    },
    {
      heading: "19. Third-Party Content and Attributions",
      content: [
        `Some imagery used on the Platform is sourced from Unsplash and used under the Unsplash License (https://unsplash.com/license), which permits use for commercial and non-commercial purposes without attribution. We acknowledge the photographers whose work appears across the Platform:`,
        {
          type: "list",
          items: [
            `Photos by Jeremy Bishop on Unsplash (https://unsplash.com/@jeremybishop)`,
          ],
        },
        `Other third-party assets, libraries, and services used by the Platform remain the property of their respective owners and are used in accordance with their applicable licences.`,
      ],
    },
    {
      heading: "20. Contact",
      content: [
        `If you have any questions about these Terms, please contact us:`,
        {
          type: "list",
          items: [
            `Email: privacy@agapae.gr`,
            `Company: EnQube SMPC`,
            `Registered office: Patras, Greece`,
          ],
        },
      ],
    },
  ],
};

export default termsEN;
